import { request } from "../client";

import type {
  TextListResponse,
  Text,
  TextFilters,
  TextGenerationRequest,
  GeneratedTextResponse,
} from "../types/text";

export function createTextsEndpoints(baseUrl: string): {
  list: (
    filters?: TextFilters,
    token?: string | null,
  ) => Promise<TextListResponse>;
  getById: (
    id: string,
    token?: string | null,
  ) => Promise<
    { text: Text; access: { hasAccess: boolean; reason?: string } } | Text
  >;
  generate: (
    req: TextGenerationRequest,
    token?: string | null,
  ) => Promise<GeneratedTextResponse>;
  toggleFavorite: (
    id: string,
    token?: string | null,
  ) => Promise<{ favorited: boolean }>;
  rate: (
    id: string,
    rating: number,
    token?: string | null,
  ) => Promise<{ success: boolean; averageRating: number }>;
  getTranslation: (
    id: string,
    targetLanguage: string,
    token?: string | null,
  ) => Promise<{
    translation: string;
    targetLanguage: string;
    fromCache: boolean;
  }>;
  getUserTranslation: (
    id: string,
    token?: string | null,
  ) => Promise<{
    translation: string;
    targetLanguage: string;
    fromCache: boolean;
  }>;
  delete: (id: string, token?: string | null) => Promise<{ message: string }>;
} {
  return {
    /**
     * Получить список текстов с фильтрами
     */
    list(
      filters: TextFilters = {},
      token?: string | null,
    ): Promise<TextListResponse> {
      return request<TextListResponse>(baseUrl, "/texts", {
        method: "GET",
        query: filters as Record<string, string | number | boolean | undefined>,
        token,
      });
    },

    /**
     * Получить конкретный текст по ID
     */
    getById(
      id: string,
      token?: string | null,
    ): Promise<
      { text: Text; access: { hasAccess: boolean; reason?: string } } | Text
    > {
      return request<
        { text: Text; access: { hasAccess: boolean; reason?: string } } | Text
      >(baseUrl, `/texts/${id}`, {
        method: "GET",
        token,
      });
    },

    /**
     * Сгенерировать новый текст с помощью AI
     */
    generate(
      req: TextGenerationRequest,
      token?: string | null,
    ): Promise<GeneratedTextResponse> {
      return request<GeneratedTextResponse>(baseUrl, "/texts/generate", {
        method: "POST",
        body: req,
        token,
      });
    },

    /**
     * Добавить/убрать текст из избранного
     */
    toggleFavorite(
      id: string,
      token?: string | null,
    ): Promise<{ favorited: boolean }> {
      return request<{ favorited: boolean }>(baseUrl, `/texts/${id}/favorite`, {
        method: "POST",
        token,
      });
    },

    /**
     * Оценить текст
     */
    rate(
      id: string,
      rating: number,
      token?: string | null,
    ): Promise<{ success: boolean; averageRating: number }> {
      return request<{ success: boolean; averageRating: number }>(
        baseUrl,
        `/texts/${id}/rate`,
        {
          method: "POST",
          body: { rating },
          token,
        },
      );
    },

    /**
     * Получить перевод текста
     */
    getTranslation(
      id: string,
      targetLanguage: string,
      token?: string | null,
    ): Promise<{
      translation: string;
      targetLanguage: string;
      fromCache: boolean;
    }> {
      return request<{
        translation: string;
        targetLanguage: string;
        fromCache: boolean;
      }>(baseUrl, `/texts/${id}/translation/${targetLanguage}`, {
        method: "GET",
        token,
      });
    },

    /**
     * Получить перевод на родной язык пользователя
     */
    getUserTranslation(
      id: string,
      token?: string | null,
    ): Promise<{
      translation: string;
      targetLanguage: string;
      fromCache: boolean;
    }> {
      return request<{
        translation: string;
        targetLanguage: string;
        fromCache: boolean;
      }>(baseUrl, `/texts/${id}/translation`, {
        method: "GET",
        token,
      });
    },

    /**
     * Удалить текст (только автор)
     */
    delete(id: string, token?: string | null): Promise<{ message: string }> {
      return request<{ message: string }>(baseUrl, `/texts/${id}`, {
        method: "DELETE",
        token,
      });
    },
  };
}
